import { useFormikContext } from "formik";
import { useMemo } from "react";
import { Merchandise, MerchandiseCatalogItem } from "types";
import { MerchandiseItemsFormValues } from "../merchandiseHelper";
import { useVariantBulkSave } from "./useVariantBulkSave";
import { useVariantCatalogData } from "./useVariantCatalogData";
import { useVariantSelection } from "./useVariantSelection";
import { useVariantTree } from "./useVariantTree";
import { getCatalogItemsWithDraftValues, VariantDraftItems } from "./variantGroupItemInventoryPayloadHelper";

export const useVariantCatalogItems = (merchandise: Merchandise, fetchedCatalogItems?: MerchandiseCatalogItem[]) => {
    const { values } = useFormikContext<MerchandiseItemsFormValues>();

    const {
        catalogItems,
        setCatalogItems,
        expandedItems,
        setExpandedItems,
        originalCatalogIdentityByIdRef,
        publicVariantCount,
        leafItemIds,
    } = useVariantCatalogData(merchandise, fetchedCatalogItems);

    const { tree, rootItems } = useVariantTree(catalogItems);

    const selection = useVariantSelection(leafItemIds);

    // Merge draft values only for consumers that need the full merged list
    // (e.g. EditVariantModal's variantPath, saveChangedCatalogItems).
    // The tree and row rendering do NOT depend on this.
    const catalogItemsWithDraftValues = useMemo(() => {
        const variantItems: VariantDraftItems = values.variantItems ?? {};
        return getCatalogItemsWithDraftValues(catalogItems, variantItems);
    }, [catalogItems, values.variantItems]);

    const duplicateSkuItemIds = useMemo(() => {
        const skuToIds = new Map<string, string[]>();
        for (const item of catalogItemsWithDraftValues) {
            if (!item.isLeaf || !item.id) continue;
            const sku = (item.sku ?? "").trim();
            if (!sku) continue;
            if (!skuToIds.has(sku)) skuToIds.set(sku, []);
            skuToIds.get(sku)!.push(item.id);
        }
        const result = new Set<string>();
        for (const ids of skuToIds.values()) {
            if (ids.length > 1) for (const id of ids) result.add(id);
        }
        return result;
    }, [catalogItemsWithDraftValues]);

    const {
        updateSelectedStatus,
        updateSelectedPrice,
        updateSelectedOversellPolicy,
        updateSelectedLowInventory,
        saveChangedCatalogItems,
        handleSaveItem,
        serverDuplicateSkuItemIds,
    } = useVariantBulkSave({
        merchandise,
        catalogItems,
        setCatalogItems,
        selectedItems: selection.items,
        getSelectedLeafIds: selection.getSelectedLeafIds,
        originalCatalogIdentityByIdRef,
    });

    return {
        table: {
            tree,
            rootItems,
            expandedItems,
            setExpandedItems,
            catalogItemsWithDraftValues,
            publicVariantCount,
            duplicateSkuItemIds: new Set([...duplicateSkuItemIds, ...serverDuplicateSkuItemIds]),
        },
        selection,
        bulkActions: {
            updateSelectedStatus,
            updateSelectedPrice,
            updateSelectedOversellPolicy,
            updateSelectedLowInventory,
        },
        saveChangedCatalogItems,
        handleSaveItem,
    };
};
